/**
 * Compute bar positions and draw bar grid on a 2D canvas context.
 *
 * Returns array of bar metadata so consumers can also use the data for hit-
 * testing (e.g., click-on-bar to seek).
 *
 * @param {object} opts
 * @param {number} opts.bpm             beats per minute
 * @param {string} opts.timeSignature   "4/4", "3/4", "6/8"
 * @param {number} opts.durationSec     total song duration
 * @param {number} opts.barOffsetMs     lead-in offset in ms (default 0)
 * @returns {Array<{barNumber:number, startSec:number, endSec:number}>}
 */
export function computeBarPositions({ bpm, timeSignature, durationSec, barOffsetMs = 0 }) {
  if (!bpm || !durationSec) return [];
  const [beatsPerBar] = timeSignature.split('/').map(Number);
  const beatSec = 60 / bpm;
  const barSec = beatSec * beatsPerBar;
  const offsetSec = barOffsetMs / 1000;
  const bars = [];
  let bar = 1;
  let t = offsetSec;
  while (t < durationSec) {
    bars.push({ barNumber: bar, startSec: t, endSec: Math.min(t + barSec, durationSec) });
    t += barSec;
    bar++;
  }
  return bars;
}

/**
 * Draw bar grid + bar numbers on a 2D canvas context.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {object} opts
 * @param {number} opts.bpm
 * @param {string} opts.timeSignature
 * @param {number} opts.durationSec
 * @param {number} opts.pxPerSec      canvas width / durationSec, precomputed
 * @param {number} opts.height        canvas height in px
 * @param {number} [opts.barOffsetMs=0]
 * @param {string} [opts.lineColor='rgba(255,255,255,0.1)']
 * @param {string} [opts.labelColor='rgba(255,255,255,0.4)']
 * @param {number} [opts.labelEveryN=4]  label every N bars
 */
export function drawBarGrid(ctx, opts) {
  const {
    bpm, timeSignature, durationSec, pxPerSec, height,
    barOffsetMs = 0,
    lineColor = 'rgba(255,255,255,0.1)',
    labelColor = 'rgba(255,255,255,0.4)',
    labelEveryN = 4,
  } = opts;
  const bars = computeBarPositions({ bpm, timeSignature, durationSec, barOffsetMs });
  ctx.save();
  ctx.strokeStyle = lineColor;
  ctx.fillStyle = labelColor;
  ctx.font = '10px ui-monospace, monospace';
  for (const b of bars) {
    const x = Math.round(b.startSec * pxPerSec) + 0.5;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
    if (b.barNumber % labelEveryN === 0 || b.barNumber === 1) {
      ctx.fillText(String(b.barNumber), x + 2, 10);
    }
  }
  ctx.restore();
}
